/**
 * Notification API Service
 * 
 * This service handles all HTTP API calls to the backend notification endpoints.
 * It uses the centralized apiClient which includes automatic token refresh logic.
 */

import axios, { AxiosError } from 'axios';
import { apiClient } from '@/app/utils/apiClient';
import { Notification, GetUserNotificationsRequest, GetUserNotificationsResponse, GetUnreadCountResponse, SendBulkNotificationRequest, SearchNotificationsRequest, ApiResponse, ApiErrorResponse } from '../types/notification.types';

/**
 * Notification Service Configuration
 */
interface NotificationServiceConfig {
  baseURL: string;
  getAccessToken: () => string | null;
}

/**
 * Notification Service Class
 * Now uses centralized apiClient for automatic token refresh
 */
class NotificationService {
  private initialized: boolean = false;

  /**
   * Initialize the notification service
   * Note: This is now a no-op since we use the centralized apiClient
   * Kept for backward compatibility
   */
  public initialize(_config: NotificationServiceConfig): void {
    this.initialized = true;
    console.log('[NotificationService] Initialized with centralized apiClient (automatic token refresh enabled)');
  }

  /**
   * Ensure service is initialized
   */
  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('NotificationService not initialized. Call initialize() first.');
    }
  }

  // ============================================================================
  // User Notification Endpoints
  // ============================================================================

  /**
   * Get user notifications with pagination and filtering
   */
  public async getUserNotifications(
    request: GetUserNotificationsRequest = {}
  ): Promise<GetUserNotificationsResponse> {
    this.ensureInitialized();

    const params = {
      page: request.page || 1,
      pageSize: request.pageSize || 20,
      ...(request.isRead !== undefined && { isRead: request.isRead }),
    };

    const response = await apiClient.get<ApiResponse<GetUserNotificationsResponse>>(
      '/api/v1/notifications',
      { params }
    );

    return response.data.data;
  }

  /**
   * Get unread notification count
   * Now uses apiClient with automatic token refresh
   */
  public async getUnreadCount(): Promise<number> {
    this.ensureInitialized();

    try {
      const response = await apiClient.get<ApiResponse<GetUnreadCountResponse>>(
        '/api/v1/notifications/unread-count',
        { timeout: 5000 }
      );

      return response.data.data.unreadCount;
    } catch (error) {
      // apiClient handles token refresh automatically, so 401 errors here mean
      // the token refresh failed and user will be redirected to login
      console.warn('[NotificationService] Failed to fetch unread count:', error);
      throw error;
    }
  }

  /**
   * Get notification by ID
   */
  public async getNotificationById(id: number): Promise<Notification> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<Notification>>(
      `/api/v1/notifications/${id}`
    );

    return response.data.data;
  }

  /**
   * Mark notification as read
   */
  public async markAsRead(id: number): Promise<void> {
    this.ensureInitialized();

    await apiClient.post(`/api/v1/notifications/${id}/mark-read`);
  }

  /**
   * Dismiss notification
   */
  public async dismissNotification(id: number): Promise<void> {
    this.ensureInitialized();

    await apiClient.post(`/api/v1/notifications/${id}/dismiss`);
  }

  /**
   * Mark all notifications as read
   */
  public async markAllAsRead(): Promise<void> {
    this.ensureInitialized();

    await apiClient.post('/api/v1/notifications/mark-all-read');
  }

  /**
   * Clear all read notifications
   */
  public async clearAllRead(): Promise<void> {
    this.ensureInitialized();

    await apiClient.delete('/api/v1/notifications/clear-all');
  }

  /**
   * Search notifications with filters
   */
  public async searchNotifications(
    request: SearchNotificationsRequest
  ): Promise<GetUserNotificationsResponse> {
    this.ensureInitialized();

    const params = {
      ...(request.searchTerm && { searchTerm: request.searchTerm }),
      ...(request.priority && { priority: request.priority }),
      ...(request.category && { category: request.category }),
      ...(request.isRead !== undefined && { isRead: request.isRead }),
      ...(request.startDate && { startDate: request.startDate }),
      ...(request.endDate && { endDate: request.endDate }),
      page: request.page || 1,
      pageSize: request.pageSize || 20,
    };

    const response = await apiClient.get<ApiResponse<GetUserNotificationsResponse>>(
      '/api/v1/notifications/search',
      { params }
    );

    return response.data.data;
  }

  // ============================================================================
  // Admin Notification Endpoints
  // ============================================================================

  /**
   * Send notification to specific users (admin only)
   */
  public async sendNotification(request: any): Promise<void> {
    this.ensureInitialized();

    await apiClient.post('/api/v1/notifications/send', request);
  }

  /**
   * Send bulk notification to multiple users (admin only)
   */
  public async sendBulkNotification(request: SendBulkNotificationRequest): Promise<void> {
    this.ensureInitialized();

    await apiClient.post('/api/v1/notifications/send-bulk', request);
  }

  /**
   * Get notification delivery status (admin only)
   */
  public async getDeliveryStatus(id: number): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<any>>(
      `/api/v1/notifications/${id}/delivery-status`
    );

    return response.data.data;
  }

  /**
   * Get all delivery status records with filtering (admin only)
   */
  public async getAllDeliveryStatus(params?: {
    status?: 'all' | 'sent' | 'delivered' | 'read' | 'failed';
    page?: number;
    pageSize?: number;
  }): Promise<any> {
    this.ensureInitialized();

    const queryParams = {
      status: params?.status || 'all',
      pageNumber: params?.page || 1,
      pageSize: params?.pageSize || 50,
    };

    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/notifications/delivery-status',
      { params: queryParams }
    );

    return response.data.data;
  }

  /**
   * Get notification analytics (admin only)
   */
  public async getAnalytics(timeRange?: '24h' | '7d' | '30d' | '90d'): Promise<any> {
    this.ensureInitialized();

    const params = {
      timeRange: timeRange || '7d',
    };

    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/notifications/analytics',
      { params }
    );

    return response.data.data;
  }

  /**
   * Get quick stats for dashboard (admin only)
   */
  public async getQuickStats(): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/notifications/stats'
    );

    // Backend returns PascalCase, convert to camelCase for frontend
    const data = response.data.data;
    const result = {
      totalSent: data.TotalSent || data.totalSent || 0,
      onlineUsers: data.OnlineUsers || data.onlineUsers || 0,
      readRate: data.ReadRate || data.readRate || 0,
      failedLast24h: data.FailedLast24h || data.failedLast24h || 0,
    };
    
    return result;
  }

  // ============================================================================
  // Admin Online Users Endpoints
  // ============================================================================

  /**
   * Get all online users (admin only)
   */
  public async getOnlineUsers(params?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    tenantId?: string;
  }): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/online-users',
      { params }
    );

    return response.data.data;
  }

  /**
   * Get online users count (admin only)
   */
  public async getOnlineUsersCount(tenantId?: string): Promise<number> {
    this.ensureInitialized();

    const params = tenantId ? { tenantId } : undefined;
    const response = await apiClient.get<ApiResponse<{ count: number }>>(
      '/api/v1/admin/online-users/count',
      { params }
    );

    return response.data.data.count;
  }

  /**
   * Get online users by tenant (admin only)
   */
  public async getOnlineUsersByTenant(
    tenantId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<any>>(
      `/api/v1/admin/online-users/by-tenant/${tenantId}`,
      { params }
    );

    return response.data.data;
  }

  /**
   * Check if user is online (admin only)
   */
  public async isUserOnline(userId: number): Promise<boolean> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<{ isOnline: boolean }>>(
      `/api/v1/admin/online-users/is-online/${userId}`
    );

    return response.data.data.isOnline;
  }

  // ============================================================================
  // Admin API Key Management Endpoints
  // ============================================================================

  /**
   * Get all API keys (admin only)
   */
  public async getAllApiKeys(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/api-keys',
      { params }
    );

    return response.data.data;
  }

  /**
   * Create new API key (admin only)
   */
  public async createApiKey(request: {
    name: string;
    description?: string;
    expiresAt?: string;
    rateLimitPerMinute?: number;
    allowedScopes?: string;
    allowedIpAddresses?: string;
  }): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.post<ApiResponse<any>>(
      '/api/v1/admin/api-keys',
      request
    );

    return response.data.data;
  }

  /**
   * Revoke API key (admin only)
   */
  public async revokeApiKey(id: number): Promise<void> {
    this.ensureInitialized();

    await apiClient.delete(`/api/v1/admin/api-keys/${id}`);
  }

  /**
   * Get API key usage statistics (admin only)
   */
  public async getApiKeyUsage(
    id: number,
    params?: { startDate?: string; endDate?: string }
  ): Promise<any> {
    this.ensureInitialized();

    const response = await apiClient.get<ApiResponse<any>>(
      `/api/v1/admin/api-keys/${id}/usage`,
      { params }
    );

    return response.data.data;
  }

  // ============================================================================
  // Error Handling Utilities
  // ============================================================================

  /**
   * Extract error message from axios error
   */
  public static getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.data?.message) {
        return axiosError.response.data.message;
      }
      if (axiosError.message) {
        return axiosError.message;
      }
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'An unknown error occurred';
  }

  /**
   * Check if error is authentication error
   */
  public static isAuthError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      return error.response?.status === 401;
    }
    return false;
  }

  /**
   * Check if error is authorization error
   */
  public static isAuthorizationError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      return error.response?.status === 403;
    }
    return false;
  }

  /**
   * Check if error is not found error
   */
  public static isNotFoundError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      return error.response?.status === 404;
    }
    return false;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Export class for static methods
export { NotificationService };
